/**
 * Clock geofencing constants shared by server enforcement and settings UI.
 * The radius is not a capability — it lives on `instance_role_presets.geofence_radius_m`
 * with an optional per-membership override (`instance_memberships.geofence_radius_m`).
 */
export const DEFAULT_GEOFENCE_RADIUS_M = 200;
export const MIN_GEOFENCE_RADIUS_M = 50;
export const MAX_GEOFENCE_RADIUS_M = 2000;

export const GEOFENCE_CAPABILITY_KEYS = ['app.zeit.geofence_in', 'app.zeit.geofence_out'] as const;

/** Rounds to an integer; `null` when the input is not a finite number. */
export function parseGeofenceRadiusM(value: unknown): number | null {
  if (value == null || value === '' || typeof value === 'boolean') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n);
}

/** German validation message for settings forms; `null` when the value is in range. */
export function geofenceRadiusMValidationError(value: unknown): string | null {
  const n = parseGeofenceRadiusM(value);
  if (n === null) return 'Bitte einen Radius in Metern eingeben.';
  if (n < MIN_GEOFENCE_RADIUS_M) {
    return `Der Radius muss mindestens ${MIN_GEOFENCE_RADIUS_M} m betragen.`;
  }
  if (n > MAX_GEOFENCE_RADIUS_M) {
    return `Der Radius darf maximal ${MAX_GEOFENCE_RADIUS_M} m betragen.`;
  }
  return null;
}

/** Rounds and clamps to the DB check constraint; `null` when the input is not a finite number. */
export function clampGeofenceRadiusM(value: unknown): number | null {
  const n = parseGeofenceRadiusM(value);
  if (n === null) return null;
  return Math.min(MAX_GEOFENCE_RADIUS_M, Math.max(MIN_GEOFENCE_RADIUS_M, n));
}

/** Membership override wins, then the preset value, then the product default. */
export function resolveEffectiveGeofenceRadiusM(
  membershipRadiusM: unknown,
  presetRadiusM: unknown,
): number {
  return (
    clampGeofenceRadiusM(membershipRadiusM) ??
    clampGeofenceRadiusM(presetRadiusM) ??
    DEFAULT_GEOFENCE_RADIUS_M
  );
}
